<template>
  <div>
    <!-- LISTA DE ACTIVOS -->
    <v-card
      v-for="g in generators"
      :key="g.id"
      class="mb-3"
      outlined
      rounded="lg"
    >
      <v-card-text>
        <div class="d-flex align-center justify-space-between">
          <!-- Información del activo -->
          <div>
            <div class="text-subtitle-1 font-weight-medium generator-title">
              <span>{{ g.name }}</span>

              <div class="asset-title-actions">
                <button
                  v-if="tieneCoordenadas(g)"
                  type="button"
                  class="map-emoji-btn"
                  title="Ver ubicación en el mapa"
                  @click.stop="$emit('show-map', g)"
                >
                  🗺️
                </button>

                <button
                  type="button"
                  class="history-info-btn"
                  title="Ver histórico de ganancias"
                  @click.stop="$emit('show-history', g)"
                >
                  <span class="material-symbols-outlined history-info-icon">
                    info
                  </span>
                </button>
              </div>
            </div>

            <!-- Tecnología -->
            <div class="text-caption grey--text tech-line">
              <span>Tipo: {{ g.type }}</span>

              <span
                v-if="g.type === 'Eólica'"
                class="material-symbols-outlined tech-icon"
              >
                mode_fan
              </span>

              <span
                v-if="g.type === 'Fotovoltaica'"
                class="material-symbols-outlined tech-icon"
              >
                solar_power
              </span>

              <span
                v-if="g.type === 'Hidráulica'"
                class="material-symbols-outlined tech-icon"
              >
                water
              </span>

              <span
                v-if="g.type === 'Nuclear'"
                class="material-symbols-outlined tech-icon"
              >
                orbit
              </span>

              <span
                v-if="g.type === 'Ciclo combinado'"
                class="material-symbols-outlined tech-icon"
              >
                factory
              </span>

              <span
                v-if="g.type === 'Carbón'"
                class="material-symbols-outlined tech-icon"
              >
                local_fire_department
              </span>

              <span class="tech-cells-badge">
                {{ getTechCells(g.type) }} celdas
              </span>
            </div>

            <!-- Información de pujas -->
            <div class="mt-1">
              <v-chip
                v-if="Number(g.bidCount || 0) > 0"
                x-small
                outlined
                class="mr-2"
              >
                Pujas: {{ g.bidCount }}
              </v-chip>

              <v-chip
                v-if="g.hasMyBid"
                x-small
                color="primary"
                text-color="white"
              >
                Has pujado
              </v-chip>
            </div>
          </div>

          <!-- Precio semanal -->
          <div class="text-right generator-price-summary">
            <div class="text-caption grey--text">
              Precio mínimo
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              {{ money(g.purchasePrice) }}
            </div>

            <v-chip
              x-small
              outlined
              class="mt-1"
              :color="weeklyChangeColor(g.weeklyChangePct)"
            >
              {{ weeklyChangeLabel(g.weeklyChangePct) }}
            </v-chip>

            <div
              v-if="g.priceWeekStart && g.priceWeekEnd"
              class="text-caption grey--text mt-1 weekly-price-dates"
            >
              {{ formatShortDate(g.priceWeekStart) }}
              –
              {{ formatShortDate(g.priceWeekEnd) }}
            </div>

            <div
              v-if="
                g.weeklyPrice != null
                && Number(g.weeklyPrice) !== Number(g.purchasePrice)
              "
              class="text-caption grey--text mt-1"
            >
              Valor semanal: {{ money(g.weeklyPrice) }}
            </div>
          </div>
        </div>

        <!-- Datos del activo -->
        <v-row dense class="mt-3">
          <v-col cols="4">
            <div class="text-caption grey--text">
              Capacidad
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              {{ g.capacity }} MW
            </div>
          </v-col>

          <v-col cols="4">
            <div class="text-caption grey--text">
              Estado
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              <span
                v-if="Number(g.owned || 0) > 0"
                class="status-badge-owned"
              >
                Ya obtenido
              </span>

              <span
                v-else-if="Number(g.ext_owned || 0) > 0"
                class="status-badge-ext-owned"
              >
                Obtenido por otro
              </span>

              <span
                v-else-if="Number(budget || 0) > Number(g.purchasePrice || 0)"
              >
                Disponible
              </span>

              <span v-else class="red--text">
                Sin presupuesto
              </span>
            </div>
          </v-col>

          <v-col cols="4">
            <div class="text-caption grey--text">
              Puja mínima
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              &gt; {{ money(g.purchasePrice) }}
            </div>
          </v-col>
        </v-row>

        <!-- Acciones -->
        <v-row dense class="mt-2">
          <v-col cols="4">
            <v-btn
              block
              class="market-action-btn market-action-btn-primary"
              :disabled="isBidDisabled(g)"
              @click="openBid(g)"
            >
              {{ g.hasMyBid ? "Modificar puja" : "Pujar" }}
            </v-btn>
          </v-col>

          <v-col cols="4">
            <v-btn
              block
              outlined
              class="market-action-btn market-action-btn-cancel"
              :disabled="!canCancelBid(g)"
              @click="cancelBid(g)"
            >
              Cancelar puja
            </v-btn>
          </v-col>

          <v-col cols="4">
            <v-btn
              block
              outlined
              class="market-action-btn market-action-btn-sell"
              :disabled="!hasValidLiga || Number(g.owned || 0) <= 0"
              @click="emitSellMarket(g)"
            >
              Poner en venta
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- DIÁLOGO DE PUJA -->
    <v-dialog
      v-model="bidDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{
            selected && selected.hasMyBid
              ? "Modificar puja"
              : "Pujar por activo"
          }}
        </v-card-title>

        <v-card-text v-if="selected">
          <div class="mb-3">
            <div class="text-subtitle-2 font-weight-medium">
              {{ selected.name }}
            </div>

            <div class="text-caption grey--text mt-2">
              Precio mínimo
            </div>

            <div class="text-subtitle-2 font-weight-medium">
              {{ money(selected.purchasePrice) }}
            </div>

            <v-chip
              x-small
              outlined
              class="mt-1"
              :color="weeklyChangeColor(selected.weeklyChangePct)"
            >
              {{ weeklyChangeLabel(selected.weeklyChangePct) }}
            </v-chip>

            <div
              v-if="selected.priceWeekStart && selected.priceWeekEnd"
              class="text-caption grey--text mt-1 weekly-price-dates"
            >
              {{ formatShortDate(selected.priceWeekStart) }}
              –
              {{ formatShortDate(selected.priceWeekEnd) }}
            </div>
          </div>

          <v-text-field
            v-model.number="bidAmount"
            type="number"
            label="Importe de tu puja (€)"
            outlined
            dense
            :min="Number(selected.purchasePrice || 0) + 1"
            :error="Boolean(bidError)"
            :error-messages="bidError"
          />

          <div class="text-caption grey--text">
            Capacidad disponible: {{ money(budget) }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            outlined
            class="market-action-btn market-action-btn-cancel"
            @click="closeBid"
          >
            Cancelar
          </v-btn>

          <v-btn
            class="market-action-btn market-action-btn-primary"
            :disabled="!canSubmitBid || submittingBid"
            :loading="submittingBid"
            @click="submitBid"
          >
            Confirmar puja
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: {
    generators: { type: Array, required: true },
    budget: { type: Number, required: true },
    ligaId: { type: [Number, String], default: null },
  },

  data() {
    return {
      bidDialog: false,
      selected: null,
      bidAmount: null,
      bidError: "",
      submittingBid: false,
    };
  },

  computed: {
    canSubmitBid() {
  if (!this.selected || !this.hasValidLiga) return false;

  const amount = Number(this.bidAmount || 0);
  const minPrice = Number(this.selected.purchasePrice || 0);

  const currentBid = this.selected.hasMyBid
    ? Number(this.selected.myBidAmount || 0)
    : 0;

  const availableBudget =
    Number(this.budget || 0) + currentBid;

  if (!Number.isFinite(amount) || amount <= 0) return false;

  if (amount <= minPrice) return false;

  if (amount > availableBudget) return false;

  return true;
},
    hasValidLiga() {
      const ligaId = Number(this.ligaId);
      return Number.isInteger(ligaId) && ligaId > 0;
    },
  },

  watch: {
    ligaId(newLigaId, oldLigaId) {
      if (newLigaId === oldLigaId) return;
      this.closeBid();
    },
  },

  methods: {
    requireLigaId() {
      const ligaId = Number(this.ligaId);

      if (!Number.isInteger(ligaId) || ligaId <= 0) {
        throw new Error(
          "No se ha podido identificar la liga seleccionada."
        );
      }

      return ligaId;
    },

    formatShortDate(value) {
  if (!value) return "";

  const date = new Date(`${value}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
  });
},
    weeklyChangeColor(value) {
  const n = Number(value || 0);

  if (n > 0) return "green";
  if (n < 0) return "red";

  return "grey";
},

weeklyChangeLabel(value) {
  const n = Number(value || 0);

  if (n > 0) {
    return `↑ ${n.toLocaleString("es-ES", {
      maximumFractionDigits: 2,
    })}%`;
  }

  if (n < 0) {
    return `↓ ${Math.abs(n).toLocaleString("es-ES", {
      maximumFractionDigits: 2,
    })}%`;
  }

  return "Sin cambios";
},
    money(v) {
      try {
        return new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(v);
      } catch {
        return `${v}€`;
      }
    },
    normalizarTech(type) {
  return (type || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
},

getTechCells(type) {
  const t = this.normalizarTech(type);

  const weights = {
    eolica: 1,
    fotovoltaica: 1,
    "ciclo combinado": 3,
    hidraulica: 1,
    nuclear: 8,
    carbon: 4,
  };

  return weights[t] || 1;
},
    tieneCoordenadas(g) {
  const x = Number(g.x);
  const y = Number(g.y);

  return Number.isFinite(x) && Number.isFinite(y);
},

    isBidDisabled(g) {
      if (!this.hasValidLiga) return true;
      if (g.owned > 0) return true;
      if (g.ext_owned > 0) return true;

      const capacidad = Number(this.budget || 0);
      const precioMinimo = Number(g.purchasePrice || 0);

      if (capacidad <= precioMinimo && !g.hasMyBid) {
        return true;
      }

      return false;
    },

    canCancelBid(g) {
      if (!this.hasValidLiga) return false;
      if (g.owned > 0) return false;
      if (g.ext_owned > 0) return false;
      return !!g.hasMyBid;
    },

    openBid(g) {
      this.selected = g;
      this.bidAmount = Number(g.myBidAmount || 0) > 0
        ? Number(g.myBidAmount)
        : Number(g.purchasePrice || 0) + 1;
      this.bidError = "";
      this.bidDialog = true;
    },

    closeBid() {
      this.bidDialog = false;
      this.selected = null;
      this.bidAmount = null;
      this.bidError = "";
      this.submittingBid = false;
    },

    submitBid() {
  if (!this.selected || this.submittingBid) return;

  const amount = Number(this.bidAmount || 0);
  const minPrice = Number(this.selected.purchasePrice || 0);

  const currentBid = this.selected.hasMyBid
    ? Number(this.selected.myBidAmount || 0)
    : 0;

  const availableBudget =
    Number(this.budget || 0) + currentBid;

  if (!Number.isFinite(amount) || amount <= minPrice) {
    this.bidError = `La puja debe ser mayor que ${this.money(minPrice)}`;
    return;
  }

  if (amount > availableBudget) {
    this.bidError = "No tienes presupuesto ni crédito disponible suficiente.";
    return;
  }

  this.submittingBid = true;

  this.$emit("bid", {
    assetType: "generator",
    id: this.selected.id,
    amount,
    liga_id: this.requireLigaId(),
  });

  this.closeBid();
},

    cancelBid(g) {
      this.$emit("cancel-bid", {
        assetType: "generator",
        id: g.id,
        liga_id: this.requireLigaId(),
      });
    },

    emitSellMarket(g) {
      this.$emit("sell-market", {
        assetType: "generator",
        id: g.id,
        liga_id: this.requireLigaId(),
      });
    },
  },
};
</script>

<style scoped>
.status-badge-owned {
  padding: 2px 8px;
  border-radius: 10px;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 12px;
}
.status-badge-ext-owned {
  padding: 2px 8px;
  border-radius: 10px;
  background: #ffebee;
  color: #c62828;
  font-size: 12px;
}
.generator-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.map-emoji-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.map-emoji-btn:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 4px rgba(49, 99, 95, 0.55));
}
.asset-title-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.history-info-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.history-info-btn:hover {
  transform: scale(1.2);
  filter: drop-shadow(0 0 4px rgba(49, 99, 95, 0.55));
}

.history-info-icon {
  font-size: 18px !important;
  color: #31635f;
}
.tech-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.tech-icon {
  font-size: 17px !important;
  color: #31635f;
  vertical-align: middle;
}

.tech-cells-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(49, 99, 95, 0.12);
  color: #31635f;
  border: 1px solid rgba(49, 99, 95, 0.25);
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.generator-price-summary {
  min-width: 115px;
  margin-left: 12px;
}

.weekly-price-dates {
  font-size: 10px;
  white-space: nowrap;
}
/* Botones del mercado */
.market-action-btn {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 1px !important;
  border-radius: 7px !important;
}

/* PUJAR */
.market-action-btn-primary:not(.v-btn--disabled) {
  background-color: #31635f !important;
  border-color: #31635f !important;
  color: white !important;
}

/* CANCELAR PUJA */
.market-action-btn-cancel:not(.v-btn--disabled) {
  color: #c62828 !important;
  border-color: #c62828 !important;
  background-color: transparent !important;
}

/* PONER EN VENTA */
.market-action-btn-sell:not(.v-btn--disabled) {
  color: #31635f !important;
  border-color: #31635f !important;
  background-color: transparent !important;
}

/* Hover */
.market-action-btn-primary:not(.v-btn--disabled):hover {
  background-color: #274f4c !important;
}

.market-action-btn-cancel:not(.v-btn--disabled):hover {
  background-color: rgba(198, 40, 40, 0.06) !important;
}

.market-action-btn-sell:not(.v-btn--disabled):hover {
  background-color: rgba(49, 99, 95, 0.08) !important;
}
</style>